import React, { useEffect, useState } from "react";
import GenericTable from "../../../../components/generic-table/generic-table";
import { formatBbPrice, isEmpty } from "../../../../utils/common";

export default function OrderProductsBlock({ currency, data = [], isBBShop = false, summary }) {
    const [processedData, setProcessedData] = useState([]);

    // Function to convert ALL to EUR using the exchange rate from summary
    const allToEur = (allAmount) => {
        return (allAmount) * (summary?.exchange_rate_eur || 0.0101812); // Use summary rate or fallback
    };

    useEffect(() => {
        if (data.length > 0) {
            // Process data to add special price fields
            const enhancedData = data.map(item => {
                // Get the original price, quantity and discount percentage
                const originalPrice = item.price;
                const quantity = parseInt(item.quantity) || 1;
                const discountPercentage = parseFloat(item.product_discount_price) || 0;

                // Calculate the actual discounted price based on the discount percentage
                const discountAmount = (originalPrice * discountPercentage) / 100;
                const calculatedDiscountedPrice = originalPrice - discountAmount;

                // Calculate total prices (price × quantity)
                const totalDiscountedPrice = (calculatedDiscountedPrice * quantity).toFixed(2);
                const totalDiscountedPriceEur = allToEur(calculatedDiscountedPrice * quantity).toFixed(2);

                // Format the discount percentage text
                const discountPercentText = discountPercentage > 0 ?
                    `(-${discountPercentage.toFixed(0)}%)` : '';

                return {
                    ...item,
                    // Add the calculated unit discounted price
                    discountedPrice: calculatedDiscountedPrice.toFixed(2),

                    // Add the total prices (price × quantity)
                    totalDiscountedPrice: totalDiscountedPrice,
                    totalDiscountedPriceEur: totalDiscountedPriceEur,

                    // Add a special field that combines all price info
                    priceData: JSON.stringify({
                        originalPrice: originalPrice,
                        discountedPrice: calculatedDiscountedPrice.toFixed(2),
                        discountPercentage: discountPercentText
                    })
                };
            });

            setProcessedData(assignFallbackSku(enhancedData));
        }
    }, [data, summary]);

    return (
        <div className="flex flex-col mt-5 w-[100%]">
            {!isBBShop && <p className=' mb-2 text-black1 text-lg font-medium'>Order Items</p>}

            <GenericTable
                columns={isBBShop ? getProductsTableColumnsV2(currency, allToEur) : getProductsTableColumns(currency)}
                data={processedData.map((item, index) => ({
                    ...item,
                    index
                }))}
                className='!px-0 !pt-2'
            />
        </div>
    );
}

const getProductsTableColumns = (currency) => ([
    {
        label: 'No',
        field: 'index',
        align: 'left',
        cellComponent: ({ data = 0 }) => data + 1,
    },
    {
        label: 'Image',
        field: 'image',
        align: 'left',
        cellComponent: ({ data }) => (
            isEmpty(data) ? (
                <div className="w-9 h-9 bg-gray6" style={{ borderRadius: '5px' }} />
            ) : (
                <img src={data} style={{ borderRadius: '5px' }} className="w-9 h-9" alt="preview" />
            )
        ),
    },
    {
        label: 'Name',
        field: 'title',
        align: 'left',
        cellComponent: ({ data }) => (
            <span className="font-bold">{data}</span>
        ),
    },
    {
        label: 'Price',
        field: 'price',
        align: 'right',
        cellComponent: ({ data }) => formatBbPrice(data),
    },
    {
        label: 'Quantity',
        field: 'quantity',
        align: 'right',
    },
    {
        label: 'Product Total',
        field: 'total_price',
        align: 'right',
        cellComponent: ({ data }) => `${currency} ${formatBbPrice(data, false)}`,
    },
]);

const getProductsTableColumnsV2 = (currency, allToEur) => ([
    {
        label: 'No.',
        field: 'index',
        align: 'left',
        cellComponent: ({ data = 0 }) => data + 1,
    },
    {
        label: 'Image',
        field: 'image',
        align: 'left',
        cellComponent: ({ data }) => (
            isEmpty(data) ? (
                <div className="w-9 h-9 bg-gray6" style={{ borderRadius: '5px' }} />
            ) : (
                <img src={data} style={{ borderRadius: '5px' }} className="w-9 h-9" alt="product" />
            )
        ),
    },
    {
        label: 'SKU',
        field: '_productSku',
        align: 'left',
        cellComponent: ({ data }) => {
            return (
                <div>
                    <span>Product sku: {data}</span><br />
                    {data?.variationSku && <span>Variation sku: {data._variationSku}</span>}
                </div>
            )
        },
    },
    {
        label: 'Product Name',
        field: 'title',
        align: 'left',
        cellComponent: ({ data }) => (
            <span className="font-bold">{data}</span>
        ),
    },
    {
        label: 'Quantity',
        field: 'quantity',
        align: 'right',
    },
    {
        label: 'Price',
        field: 'priceData',
        align: 'right',
        cellComponent: (props) => {
            try {
                // Parse the JSON string to get all price info
                const priceInfo = JSON.parse(props.data);

                return (
                    <div className="flex flex-col items-end">
                        <div>
                            <span className="text-red-600 font-semibold">{formatBbPrice(priceInfo.discountedPrice)}</span>
                            {" "}&nbsp;{/* Add extra space here */}
                            <span className="line-through text-gray-500">{formatBbPrice(priceInfo.originalPrice)}</span>
                        </div>
                        {priceInfo.discountPercentage && (
                            <span className="text-xs text-red-600">{priceInfo.discountPercentage}</span>
                        )}
                    </div>
                );
            } catch (e) {
                // Fallback if parsing fails
                console.error("Error parsing price data:", e);
                return formatBbPrice(0);
            }
        },
    },

    {
        label: 'Total EUR',
        field: 'totalDiscountedPriceEur', // Use the quantity-adjusted EUR price
        align: 'right',
        cellComponent: ({ data }) => `${data} EUR`
    },
    {
        label: 'Total LEK',
        field: 'totalDiscountedPrice', // Use the quantity-adjusted LEK price
        align: 'right',
        cellComponent: ({ data = 0 }) => formatBbPrice(data),
    },
])

const assignFallbackSku = (items = []) => {
    return items.map(item => ({
        ...item,
        _productSku: item?.productSku ?? Math.floor(1000000 + Math.random() * 9000000).toString(),
        _variationSku: item?.variationSku ?? Math.floor(1000000 + Math.random() * 9000000).toString()
    }))
}